import type { NormalizedSwatchCrudPayload } from "@/util/colorPicker";
import { normalizeColorPickerSwatchEventArgs } from "@/util/colorPicker";

interface DefaultColorsCrudStore {
  addDefaultColor: (payload: unknown) => Promise<void> | void;
  editDefaultColor: (payload: unknown) => Promise<void> | void;
  deleteDefaultColor: (payload: unknown) => Promise<void> | void;
}

interface UseColorPickerSwatchCrudHandlersOptions {
  /**
   * @description Called when user clicks "Manage" in the 'custom' section, typically opens Global Settings.
   */
  onCustomAdd?: () => void;
  /**
   * @description Key used by HLColorPicker for global custom colors section.
   * @default "custom"
   */
  customSectionKey?: string;
  /**
   * @description Prefix used in error logs to make debugging easier.
   */
  logPrefix?: string;
  /**
   * @description Called after a default color is added successfully.
   */
  onDefaultAdd?: (payload: NormalizedSwatchCrudPayload) => Promise<void> | void;
}

/**
 * @description Provides DRY handlers for HLColorPicker swatch CRUD events.
 * Supports both payload-object and positional-args emit styles from Highrise.
 *
 * @param store - Store instance that implements default color CRUD actions.
 * @param options - Optional configuration for custom section behavior and logging.
 */
export const useColorPickerSwatchCrudHandlers = (
  store: DefaultColorsCrudStore,
  options: UseColorPickerSwatchCrudHandlersOptions = {}
) => {
  const customSectionKey = options.customSectionKey ?? "custom";
  const logPrefix = options.logPrefix ? `${options.logPrefix}: ` : "";

  const handleSwatchAdd = async (...args: Array<unknown>) => {
    const payload = normalizeColorPickerSwatchEventArgs(...args);
    if (payload?.sectionKey === customSectionKey) {
      options.onCustomAdd?.();
      return;
    }

    try {
      await store.addDefaultColor(payload);
      await options.onDefaultAdd?.(payload);
    } catch (error) {
      console.error(`${logPrefix}Failed to add default color`, error);
    }
  };

  const handleSwatchEdit = async (...args: Array<unknown>) => {
    const payload = normalizeColorPickerSwatchEventArgs(...args);
    try {
      await store.editDefaultColor(payload);
    } catch (error) {
      console.error(`${logPrefix}Failed to edit default color`, error);
    }
  };

  const handleSwatchDelete = async (...args: Array<unknown>) => {
    const payload = normalizeColorPickerSwatchEventArgs(...args);
    try {
      await store.deleteDefaultColor(payload);
    } catch (error) {
      console.error(`${logPrefix}Failed to delete default color`, error);
    }
  };

  return { handleSwatchAdd, handleSwatchEdit, handleSwatchDelete } as const;
};
